import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useBusiness } from '../../context/BusinessContext';
import { getMembers, createMember, updateMember, approveMember, suspendMember, deleteMember, getPlans, createMembership, checkExpiredMemberships, getGyms, setMemberQRMode, uploadAvatarAdmin, getMemberDevices, deactivateDevice, deactivateAllDevices, getMemberEmails, resendEmail, cleanupInactiveMembers, assignRFID, importMembers, assignMembershipsBulk, updateMemberMembership, getMembershipLogs, getAccessVideos } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Search, Plus, MoreVertical, Check,
  UserPlus, CreditCard, Pencil, Trash2, Ban, CheckCircle,
  AlertTriangle, RefreshCw, PauseCircle, Banknote, Receipt, QrCode, Camera,
  Mail, Phone, Copy, X as XIcon, Smartphone, Building2, Loader2, Send, Upload, FileSpreadsheet, Video
} from 'lucide-react';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../../components/ui/dropdown-menu';
import { MemberAvatar } from '../../components/MemberAvatar';

function MemberContactPopover({ member, onClose }) {
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado`);
  };

  return (
    <div className="absolute right-0 top-full mt-1 z-50 rounded-lg shadow-xl shadow-black/40 p-4 min-w-[300px] animate-in fade-in slide-in-from-top-1 duration-150"
      style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-secondary)' }}
      data-testid={`contact-popover-${member.code}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Datos del Socio</span>
        <button onClick={onClose} style={{ color: 'var(--text-muted)' }} className="hover:text-red-400 transition-colors">
          <XIcon size={14} />
        </button>
      </div>
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <span style={{ color: 'var(--text-muted)' }} className="w-20 shrink-0">Codigo:</span>
          <span className="font-mono font-bold" style={{ color: 'var(--gym-primary)' }}>{member.code}</span>
          <button onClick={() => copyToClipboard(member.code, 'Codigo')} style={{ color: 'var(--text-muted)' }} className="hover:text-white"><Copy size={12} /></button>
        </div>
        {member.email && (
          <div className="flex items-center gap-2">
            <span style={{ color: 'var(--text-muted)' }} className="w-20 shrink-0">Email:</span>
            <span className="truncate">{member.email}</span>
            <button onClick={() => copyToClipboard(member.email, 'Email')} style={{ color: 'var(--text-muted)' }} className="hover:text-white"><Copy size={12} /></button>
          </div>
        )}
        {member.phone && (
          <div className="flex items-center gap-2">
            <span style={{ color: 'var(--text-muted)' }} className="w-20 shrink-0">Telefono:</span>
            <span>{member.phone}</span>
            <button onClick={() => copyToClipboard(member.phone, 'Telefono')} style={{ color: 'var(--text-muted)' }} className="hover:text-white"><Copy size={12} /></button>
          </div>
        )}
        {member.document_id && (
          <div className="flex items-center gap-2">
            <span style={{ color: 'var(--text-muted)' }} className="w-20 shrink-0">DNI/NIE:</span>
            <span>{member.document_id}</span>
            <button onClick={() => copyToClipboard(member.document_id, 'DNI/NIE')} style={{ color: 'var(--text-muted)' }} className="hover:text-white"><Copy size={12} /></button>
          </div>
        )}
        {(member.address || member.city || member.postal_code) && (
          <div className="flex items-start gap-2">
            <span style={{ color: 'var(--text-muted)' }} className="w-20 shrink-0">Direccion:</span>
            <span className="break-words">
              {[member.address, member.postal_code, member.city].filter(Boolean).join(', ')}
            </span>
          </div>
        )}
        {member.membership_plan_name && (
          <div className="flex items-center gap-2">
            <span style={{ color: 'var(--text-muted)' }} className="w-20 shrink-0">Plan:</span>
            <span>{member.membership_plan_name}</span>
          </div>
        )}
        {member.membership_end_date && (
          <div className="flex items-center gap-2">
            <span style={{ color: 'var(--text-muted)' }} className="w-20 shrink-0">Vence:</span>
            <span>{new Date(member.membership_end_date).toLocaleDateString('es')}</span>
          </div>
        )}
        {member.created_at && (
          <div className="flex items-center gap-2">
            <span style={{ color: 'var(--text-muted)' }} className="w-20 shrink-0">Registro:</span>
            <span>{new Date(member.created_at).toLocaleDateString('es')}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminMembers() {
  const { admin, isSuperAdmin, hasPermission } = useAuth();
  const { labels } = useBusiness();
  const [members, setMembers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [newMember, setNewMember] = useState({ name: '', email: '', phone: '', gym_id: admin?.gym_id || '', gender: 'prefer_not_to_say', document_id: '', address: '', city: '', postal_code: '', license_code: '', license_issue_date: '', license_expiry_date: '' });
  const [editData, setEditData] = useState({ name: '', email: '', phone: '', can_bring_guests: false, max_guests_per_month: 2, guest_valid_days: 1, document_id: '', address: '', city: '', postal_code: '', license_code: '', license_issue_date: '', license_expiry_date: '' });
  const [memberFormResponses, setMemberFormResponses] = useState([]);
  const [expandedContact, setExpandedContact] = useState(null);
  const [showDevicesModal, setShowDevicesModal] = useState(false);
  const [showEmailsModal, setShowEmailsModal] = useState(false);
  const [memberEmails, setMemberEmails] = useState([]);
  const [resendingId, setResendingId] = useState(null);
  const [selectedGymFilter, setSelectedGymFilter] = useState('all');
  const [devicesMember, setDevicesMember] = useState(null);
  const [memberDevices, setMemberDevices] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [gyms, setGyms] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importGymId, setImportGymId] = useState('');
  const [importKeepCodes, setImportKeepCodes] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignFile, setAssignFile] = useState(null);
  const [assignGymId, setAssignGymId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignResult, setAssignResult] = useState(null);
  const [assignPreview, setAssignPreview] = useState(null);
  const assignFileRef = useRef(null);
  const [showEditExpirationModal, setShowEditExpirationModal] = useState(false);
  const [membershipMember, setMembershipMember] = useState(null);
  const [membershipDate, setMembershipDate] = useState('');
  const [membershipComment, setMembershipComment] = useState('');
  const [savingMembership, setSavingMembership] = useState(false);
  const [membershipLogs, setMembershipLogs] = useState([]);
  const [showVideosModal, setShowVideosModal] = useState(false);
  const [memberVideos, setMemberVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [playingVideoUrl, setPlayingVideoUrl] = useState(null);
  const [showRfidModal, setShowRfidModal] = useState(false);
  const [rfidInput, setRfidInput] = useState('');
  const [rfidListening, setRfidListening] = useState(false);
  const [savingRfid, setSavingRfid] = useState(false);
  const photoInputRef = useRef(null);
  const [photoTargetMemberId, setPhotoTargetMemberId] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const triggerPhotoUpload = (memberId) => {
    setPhotoTargetMemberId(memberId);
    // Timeout ensures dropdown closes before file picker opens
    setTimeout(() => photoInputRef.current?.click(), 100);
  };

  const handlePhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset input
    if (!file || !photoTargetMemberId) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('La imagen no puede superar 2MB'); return; }
    setUploadingPhoto(true);
    try {
      await uploadAvatarAdmin(photoTargetMemberId, file);
      toast.success('Foto actualizada');
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir foto');
    } finally {
      setUploadingPhoto(false);
      setPhotoTargetMemberId(null);
    }
  };

  useEffect(() => { fetchMembers(); fetchPlans(); if (isSuperAdmin) fetchGyms(); }, [statusFilter]);

  useEffect(() => {
    if (showVideosModal && selectedMember) {
      setLoadingVideos(true);
      setPlayingVideoUrl(null);
      getAccessVideos(selectedMember.id)
        .then(res => setMemberVideos(res.data))
        .catch(() => toast.error('Error al cargar videos'))
        .finally(() => setLoadingVideos(false));
    }
  }, [showVideosModal, selectedMember]);

  const fetchGyms = async () => {
    try { const res = await getGyms(); setGyms(res.data); if (res.data.length > 0 && !newMember.gym_id) setNewMember(prev => ({ ...prev, gym_id: res.data[0].id })); }
    catch (error) { console.error('Error fetching gyms:', error); }
  };

  const fetchMembers = async () => {
    try {
      const gymId = isSuperAdmin ? null : admin?.gym_id;
      const status = statusFilter === 'all' ? null : statusFilter;
      const response = await getMembers(gymId, status);
      setMembers(response.data);
    } catch (error) {
      toast.error('Error al cargar socios');
    } finally { setLoading(false); }
  };

  const fetchPlans = async () => {
    try {
      const gymId = isSuperAdmin ? null : admin?.gym_id;
      const response = await getPlans(gymId);
      setPlans(response.data);
    } catch (error) { console.error('Error fetching plans:', error); }
  };

  const handleCreateMember = async () => {
    if (!newMember.name || !newMember.email) { toast.error('Nombre y email son requeridos'); return; }
    try {
      const gymId = isSuperAdmin ? newMember.gym_id : admin?.gym_id;
      await createMember({ ...newMember, gym_id: gymId });
      toast.success('Socio creado exitosamente');
      setShowCreateModal(false);
      setNewMember({ name: '', email: '', phone: '', gym_id: '', gender: 'prefer_not_to_say', document_id: '', address: '', city: '', postal_code: '', license_code: '', license_issue_date: '', license_expiry_date: '' });
      fetchMembers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al crear socio'); }
  };

  const handleOpenEdit = async (member) => {
    setSelectedMember(member);
    setEditData({
      name: member.name,
      email: member.email || '',
      phone: member.phone || '',
      can_bring_guests: member.can_bring_guests || false,
      max_guests_per_month: member.max_guests_per_month || 2,
      guest_valid_days: member.guest_valid_days || 1,
      document_id: member.document_id || '',
      address: member.address || '',
      city: member.city || '',
      postal_code: member.postal_code || '',
      license_code: member.license_code || '',
      license_issue_date: member.license_issue_date || '',
      license_expiry_date: member.license_expiry_date || ''
    });
    setMemberFormResponses([]);
    setShowEditModal(true);
    // Load custom form responses
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/members/${member.id}/form-responses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMemberFormResponses(data || []);
      }
    } catch (e) { /* silent */ }
  };

  const handleUpdateMember = async () => {
    if (!editData.name) { toast.error('El nombre es requerido'); return; }
    try {
      await updateMember(selectedMember.id, editData);
      toast.success('Socio actualizado');
      setShowEditModal(false);
      fetchMembers();
    } catch (error) { toast.error('Error al actualizar'); }
  };

  const handleApprove = async (memberId) => {
    try { await approveMember(memberId); toast.success('Socio aprobado'); fetchMembers(); }
    catch (error) { toast.error('Error al aprobar socio'); }
  };

  const handleActivate = async (memberId) => {
    try { await updateMember(memberId, { status: 'active', suspension_reason: null }); toast.success('Socio activado'); fetchMembers(); }
    catch (error) { toast.error('Error al activar socio'); }
  };

  const handleOpenSuspend = (member) => {
    setSelectedMember(member);
    setSuspendReason('');
    setShowSuspendModal(true);
  };

  const handleSuspend = async () => {
    if (!suspendReason.trim()) { toast.error('Indica el motivo de la suspensión'); return; }
    try {
      await suspendMember(selectedMember.id, suspendReason);
      toast.success('Socio suspendido');
      setShowSuspendModal(false);
      fetchMembers();
    } catch (error) { toast.error('Error al suspender'); }
  };

  const handleOpenDelete = (member) => {
    setSelectedMember(member);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    try {
      await deleteMember(selectedMember.id);
      toast.success('Socio eliminado permanentemente');
      setShowDeleteConfirm(false);
      fetchMembers();
    } catch (error) { toast.error('Error al eliminar'); }
  };

  const handleCheckExpired = async () => {
    try {
      const res = await checkExpiredMemberships();
      toast.success(res.data.message);
      fetchMembers();
    } catch (error) { toast.error('Error al verificar'); }
  };

  const handleOpenPayment = (member) => {
    setSelectedMember(member);
    setSelectedPlan('');
    setPaymentMethod('cash');
    setPaymentNotes('');
    setShowPaymentModal(true);
  };

  const handleManualPayment = async () => {
    if (!selectedPlan) { toast.error('Selecciona un plan'); return; }
    const plan = plans.find(p => p.id === selectedPlan);
    if (!plan) return;
    try {
      const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
      await fetch(`${API_URL}/payments/manual`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          member_id: selectedMember.id,
          plan_id: selectedPlan,
          payment_method: paymentMethod,
          amount: plan.price,
          notes: paymentNotes || null
        })
      }).then(r => r.json());
      toast.success('Pago registrado y membresía activada');
      setShowPaymentModal(false);
      fetchMembers();
    } catch (error) { toast.error('Error al registrar pago'); }
  };

  useEffect(() => {
    if (!expandedContact) return;
    const handler = (e) => {
      if (!e.target.closest(`[data-testid="contact-popover-${members.find(m => m.id === expandedContact)?.code}"]`) &&
          !e.target.closest(`[data-testid^="contact-btn-"]`)) {
        setExpandedContact(null);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [expandedContact, members]);

  const handleOpenDevices = async (member) => {
    setDevicesMember(member);
    setShowDevicesModal(true);
    try {
      const res = await getMemberDevices(member.id);
      setMemberDevices(res.data);
    } catch { setMemberDevices([]); }
  };

  const handleOpenEmails = async (member) => {
    setSelectedMember(member);
    setShowEmailsModal(true);
    try {
      const res = await getMemberEmails(member.id);
      setMemberEmails(res.data);
    } catch { setMemberEmails([]); }
  };

  const handleResendEmail = async (emailId) => {
    setResendingId(emailId);
    try {
      await resendEmail(emailId);
      toast.success('Email reenviado exitosamente');
      const res = await getMemberEmails(selectedMember.id);
      setMemberEmails(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al reenviar');
    } finally { setResendingId(null); }
  };

  const handleDeactivateDevice = async (deviceId) => {
    try {
      await deactivateDevice(deviceId);
      toast.success('Dispositivo desactivado');
      const res = await getMemberDevices(devicesMember.id);
      setMemberDevices(res.data);
    } catch { toast.error('Error al desactivar'); }
  };

  const handleDeactivateAll = async () => {
    if (!devicesMember) return;
    try {
      await deactivateAllDevices(devicesMember.id);
      toast.success('Todos los dispositivos desactivados');
      const res = await getMemberDevices(devicesMember.id);
      setMemberDevices(res.data);
    } catch { toast.error('Error'); }
  };

  const filteredMembers = members.filter(m => {
    const matchSearch = (m.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (m.email || '').toLowerCase().includes(search.toLowerCase()) ||
      m.code.toLowerCase().includes(search.toLowerCase());
    const matchGym = selectedGymFilter === 'all' || m.gym_id === selectedGymFilter;
    return matchSearch && matchGym;
  });

  const getGymName = (gymId) => {
    const gym = gyms.find(g => g.id === gymId);
    return gym?.name || '';
  };

  const handleImport = async () => {
    if (!importFile) { toast.error('Selecciona un archivo Excel'); return; }
    if (!importGymId) { toast.error('Selecciona un gimnasio destino'); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('gym_id', importGymId);
      formData.append('keep_codes', importKeepCodes);
      const res = await importMembers(formData);
      setImportResult(res.data);
      toast.success(res.data.message);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en la importacion');
      setImportResult({ success: false, message: error.response?.data?.detail || 'Error' });
    } finally { setImporting(false); }
  };

  const handleAssignBulk = async () => {
    if (!assignPreview || !assignGymId) { toast.error('Selecciona gym y archivo'); return; }
    setAssigning(true); setAssignResult(null);
    try {
      const res = await assignMembershipsBulk({ gym_id: assignGymId, vencimientos: assignPreview });
      setAssignResult(res.data);
      toast.success(res.data.message);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally { setAssigning(false); }
  };

  const handleUpdateMembership = async () => {
    if (!membershipMember || !membershipDate) { toast.error('Fecha requerida'); return; }
    setSavingMembership(true);
    try {
      await updateMemberMembership(membershipMember.id, { end_date: membershipDate, comment: membershipComment });
      toast.success('Vencimiento actualizado');
      setShowMembershipModal(false);
      setMembershipComment('');
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally { setSavingMembership(false); }
  };

  const getStatusBadge = (status, member) => {
    const badges = { active: 'badge-success', pending: 'badge-warning', blocked: 'badge-danger', suspended: 'bg-orange-500/20 text-orange-400 border border-orange-500/30' };
    const labels = { active: 'Activo', pending: 'Pendiente', blocked: 'Bloqueado', suspended: 'Suspendido' };
    let label = labels[status] || status;
    if (status === 'suspended' && member?.suspension_type === 'payment') {
      label = 'Susp. Pago';
    }
    return <span className={`badge ${badges[status] || 'badge-warning'}`}>{label}</span>;
  };

  return (
    <div className="space-y-6" data-testid="admin-members">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black tracking-tight">{labels.members}</h1>
          <p style={{ color: 'var(--text-secondary)' }} className="text-sm">{filteredMembers.length} {labels.members.toLowerCase()} registrados</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isSuperAdmin && gyms.length > 0 && (
            <select value={selectedGymFilter} onChange={e => setSelectedGymFilter(e.target.value)}
              className="input-gym text-sm h-10 min-w-[180px]" data-testid="gym-filter-select">
              <option value="all">Todos los gimnasios</option>
              {gyms.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          )}
          <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={handleCheckExpired}
            data-testid="check-expired-btn" title="Suspender socios con membresia vencida">
            <RefreshCw size={16} className="mr-2" /> Verificar Vencidos
          </Button>
          <Button variant="outline" className="border-red-800 text-red-400 hover:bg-red-900/30" 
            onClick={async () => {
              if (!window.confirm('Esto eliminara socios inactivos 60+ dias sin pagos ni accesos. Continuar?')) return;
              try {
                const res = await cleanupInactiveMembers(60);
                toast.success(res.data.message);
                fetchMembers();
              } catch (err) { toast.error('Error al limpiar socios'); }
            }}
            data-testid="cleanup-inactive-btn" title="Eliminar socios inactivos 60+ dias">
            <Trash2 size={16} className="mr-2" /> Limpiar Inactivos
          </Button>
          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button className="btn-gym-primary" data-testid="create-member-btn">
                <Plus size={20} className="mr-2" /> Nuevo {labels.member}
              </Button>
            </DialogTrigger>
          {isSuperAdmin && (
            <Button variant="outline" className="border-emerald-700 text-emerald-400 hover:bg-emerald-900/30"
              onClick={() => { setShowImportModal(true); setImportResult(null); setImportFile(null); }}
              data-testid="import-members-btn">
              <Upload size={16} className="mr-2" /> Importar Socios
            </Button>
          )}
          {isSuperAdmin && (
            <Button variant="outline" className="border-amber-700 text-amber-400 hover:bg-amber-900/30"
              onClick={() => { setShowAssignModal(true); setAssignResult(null); setAssignFile(null); setAssignPreview(null); }}
              data-testid="assign-memberships-btn">
              <FileSpreadsheet size={16} className="mr-2" /> Asignar Membresias
            </Button>
          )}
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader><DialogTitle>Crear Nuevo {labels.member}</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                {isSuperAdmin && (
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 block">Gimnasio</label>
                    <Select value={newMember.gym_id} onValueChange={(v) => setNewMember({ ...newMember, gym_id: v })}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="member-gym-select">
                        <SelectValue placeholder="Seleccionar gimnasio" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-700">
                        {gyms.map((g) => (
                          <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Nombre</label>
                  <Input value={newMember.name} onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                    placeholder="Nombre completo" className="input-dark" data-testid="member-name-input" />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Email</label>
                  <Input type="email" value={newMember.email} onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                    placeholder="email@ejemplo.com" className="input-dark" data-testid="member-email-input" />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Telefono (opcional)</label>
                  <Input value={newMember.phone} onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })}
                    placeholder="+1 234 567 890" className="input-dark" />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Sexo</label>
                  <select className="input-gym w-full" value={newMember.gender} onChange={e => setNewMember({ ...newMember, gender: e.target.value })} data-testid="member-gender-select">
                    <option value="male">Hombre</option>
                    <option value="female">Mujer</option>
                    <option value="prefer_not_to_say">Prefiero no contestar</option>
                  </select>
                </div>
                <div className="border-t border-zinc-800 pt-4">
                  <p className="text-xs text-zinc-500 mb-3 uppercase tracking-wider">Datos personales</p>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-zinc-400 mb-1 block">DNI / NIE / Pasaporte</label>
                      <Input value={newMember.document_id} onChange={(e) => setNewMember({ ...newMember, document_id: e.target.value })}
                        placeholder="12345678A" className="input-dark" data-testid="member-document-input" />
                    </div>
                    <div>
                      <label className="text-sm text-zinc-400 mb-1 block">Direccion</label>
                      <Input value={newMember.address} onChange={(e) => setNewMember({ ...newMember, address: e.target.value })}
                        placeholder="Calle, numero, piso..." className="input-dark" data-testid="member-address-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm text-zinc-400 mb-1 block">Ciudad</label>
                        <Input value={newMember.city} onChange={(e) => setNewMember({ ...newMember, city: e.target.value })}
                          placeholder="Madrid" className="input-dark" data-testid="member-city-input" />
                      </div>
                      <div>
                        <label className="text-sm text-zinc-400 mb-1 block">Codigo Postal</label>
                        <Input value={newMember.postal_code} onChange={(e) => setNewMember({ ...newMember, postal_code: e.target.value })}
                          placeholder="28001" className="input-dark" data-testid="member-postal-input" />
                      </div>
                    </div>
                    <div className="pt-3 border-t border-zinc-800">
                      <p className="text-xs uppercase tracking-wide text-zinc-500 mb-2">Licencia (opcional)</p>
                      <div>
                        <label className="text-sm text-zinc-400 mb-1 block">Codigo de Licencia</label>
                        <Input value={newMember.license_code} onChange={(e) => setNewMember({ ...newMember, license_code: e.target.value })}
                          placeholder="LIC-2026-0001" className="input-dark" data-testid="member-license-code-input" />
                      </div>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div>
                          <label className="text-sm text-zinc-400 mb-1 block">Fecha de Expedicion</label>
                          <Input type="date" value={newMember.license_issue_date} onChange={(e) => setNewMember({ ...newMember, license_issue_date: e.target.value })}
                            className="input-dark" data-testid="member-license-issue-input" />
                        </div>
                        <div>
                          <label className="text-sm text-zinc-400 mb-1 block">Fecha de Caducidad</label>
                          <Input type="date" value={newMember.license_expiry_date} onChange={(e) => setNewMember({ ...newMember, license_expiry_date: e.target.value })}
                            className="input-dark" data-testid="member-license-expiry-input" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <Button onClick={handleCreateMember} className="w-full btn-gym-primary" data-testid="save-member-btn">
                  <UserPlus size={20} className="mr-2" /> Crear Socio
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, email o código..." className="input-dark pl-10" data-testid="search-members" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-zinc-900 border-zinc-700" data-testid="status-filter">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-700">
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Activos</SelectItem>
            <SelectItem value="pending">Pendientes</SelectItem>
            <SelectItem value="suspended">Suspendidos</SelectItem>
            <SelectItem value="blocked">Bloqueados</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="stat-card overflow-visible">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800">
              <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase tracking-wider">Socio</th>
              <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase tracking-wider">Codigo</th>
              {isSuperAdmin && <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase tracking-wider">Gimnasio</th>}
              <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase tracking-wider">Plan</th>
              <th className="text-center p-3 text-zinc-400 font-medium text-xs uppercase tracking-wider">Vencimiento</th>
              <th className="text-center p-3 text-zinc-400 font-medium text-xs uppercase tracking-wider">Estado</th>
              <th className="text-center p-3 text-zinc-400 font-medium text-xs uppercase tracking-wider w-[100px]">Contacto</th>
              <th className="text-right p-3 w-[50px]"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-8"><div className="skeleton h-4 w-32 mx-auto" /></td></tr>
            ) : filteredMembers.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-zinc-500 py-8">No se encontraron socios</td></tr>
            ) : (
              filteredMembers.map((member) => (
                <tr key={member.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/20 transition-colors group">
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <MemberAvatar member={member} size={36} />
                      <div className="min-w-0">
                        <span className="font-medium text-white block truncate">{member.name}</span>
                        {member.status === 'suspended' && member.suspension_reason && (
                          <p className="text-[11px] text-orange-400 truncate max-w-[200px]" data-testid={`member-suspend-reason-${member.code}`}>
                            {member.suspension_reason}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <code className="text-xs px-2 py-0.5 rounded font-mono" style={{ background: 'var(--bg-tertiary)', color: 'var(--gym-primary)' }}>{member.code}</code>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            className={`text-[10px] px-1.5 py-0.5 rounded leading-none border transition-colors ${
                              member.license_code
                                ? 'bg-blue-900/30 text-blue-400 hover:bg-blue-900/50 hover:text-blue-300 border-blue-500/30'
                                : 'bg-zinc-800/50 text-zinc-500 hover:bg-zinc-700 hover:text-zinc-300 border-zinc-700 border-dashed'
                            }`}
                            data-testid={`member-license-badge-${member.code}`}
                            title="Ver / editar licencia"
                          >
                            Cod. Licencia
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-64 bg-zinc-900 border-zinc-700 text-sm" data-testid={`member-license-popover-${member.code}`}>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between pb-2 border-b border-zinc-800">
                              <span className="text-xs uppercase tracking-wider text-blue-400 font-semibold">Licencia</span>
                              <button onClick={() => handleOpenEdit(member)} className="text-[10px] text-orange-400 hover:text-orange-300" data-testid={`member-license-edit-${member.code}`}>
                                Editar
                              </button>
                            </div>
                            {member.license_code ? (
                              <>
                                <div>
                                  <div className="text-[10px] uppercase text-zinc-500">Codigo</div>
                                  <div className="font-mono text-white">{member.license_code}</div>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <div className="text-[10px] uppercase text-zinc-500">Expedicion</div>
                                    <div className="text-white">{member.license_issue_date ? new Date(member.license_issue_date).toLocaleDateString('es') : '—'}</div>
                                  </div>
                                  <div>
                                    <div className="text-[10px] uppercase text-zinc-500">Caducidad</div>
                                    <div className={`${member.license_expiry_date && new Date(member.license_expiry_date) < new Date() ? 'text-red-400' : 'text-white'}`}>
                                      {member.license_expiry_date ? new Date(member.license_expiry_date).toLocaleDateString('es') : '—'}
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="text-zinc-500 text-xs py-2">
                                Este socio aun no tiene licencia asignada. Pincha en &quot;Editar&quot; para agregarla.
                              </div>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                      {member.qr_mode === 'static' && <span className="text-[10px] bg-cyan-900/30 text-cyan-400 px-1.5 py-0.5 rounded leading-none">QR Fijo</span>}
                      {member.rfid_uid && <span className="text-[10px] bg-orange-900/30 text-orange-400 px-1.5 py-0.5 rounded leading-none" title={`RFID: ${member.rfid_uid}`}>RFID</span>}
                    </div>
                  </td>
                  {isSuperAdmin && (
                    <td className="p-3">
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{getGymName(member.gym_id)}</span>
                    </td>
                  )}
                  <td className="p-3">
                    {member.membership ? (
                      <span className="text-xs text-zinc-300">{member.membership.plan_name || '-'}</span>
                    ) : (
                      <span className="text-xs text-zinc-600">Sin plan</span>
                    )}
                  </td>
                  <td className="p-3 text-center">
                    {member.membership?.end_date ? (
                      <span 
                        onClick={() => { 
                          setMembershipMember(member); 
                          setMembershipDate(member.membership.end_date); 
                          setMembershipComment('');
                          setMembershipLogs([]);
                          setShowEditExpirationModal(true);
                          getMembershipLogs(member.id).then(res => setMembershipLogs(res.data)).catch(() => {});
                        }}
                        className={`text-xs font-medium px-2 py-0.5 rounded cursor-pointer hover:opacity-80 transition-opacity ${
                          new Date(member.membership.end_date) < new Date() 
                            ? 'bg-red-900/30 text-red-400' 
                            : new Date(member.membership.end_date) < new Date(Date.now() + 7 * 86400000) 
                              ? 'bg-yellow-900/30 text-yellow-400' 
                              : 'bg-emerald-900/30 text-emerald-400'
                        }`}
                        title="Clic para editar vencimiento"
                        data-testid={`edit-membership-${member.id}`}
                      >
                        {new Date(member.membership.end_date).toLocaleDateString('es-ES')}
                      </span>
                    ) : (
                      <span className="text-xs text-zinc-600">-</span>
                    )}
                  </td>
                  <td className="p-3 text-center">{getStatusBadge(member.status, member)}</td>
                  <td className="p-3 text-center relative">
                    <button
                      onClick={(e) => { e.stopPropagation(); setExpandedContact(expandedContact === member.id ? null : member.id); }}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white transition-all border border-zinc-700/50 hover:border-zinc-600"
                      data-testid={`contact-btn-${member.code}`}
                    >
                      <Mail size={12} />
                      <span>Ver</span>
                    </button>
                    {expandedContact === member.id && (
                      <MemberContactPopover member={member} onClose={() => setExpandedContact(null)} />
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 opacity-60 group-hover:opacity-100 transition-opacity" data-testid={`member-actions-${member.code}`}>
                          <MoreVertical size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-700">
                        {hasPermission('members_edit') && (
                          <DropdownMenuItem onClick={() => handleOpenEdit(member)} className="cursor-pointer" data-testid={`member-edit-${member.code}`}>
                            <Pencil size={16} className="mr-2" /> Editar
                          </DropdownMenuItem>
                        )}
                        {hasPermission('payments_register') && (
                          <DropdownMenuItem onClick={() => { setSelectedMember(member); setShowMembershipModal(true); }} className="cursor-pointer">
                            <CreditCard size={16} className="mr-2" /> Asignar Membresía
                          </DropdownMenuItem>
                        )}
                        {hasPermission('payments_register') && (
                          <DropdownMenuItem onClick={() => handleOpenPayment(member)} className="cursor-pointer text-emerald-400" data-testid={`member-payment-${member.code}`}>
                            <Banknote size={16} className="mr-2" /> Registrar Pago
                          </DropdownMenuItem>
                        )}
                        {member.status === 'pending' && hasPermission('members_edit') && (
                          <DropdownMenuItem onClick={() => handleApprove(member.id)} className="cursor-pointer text-emerald-500">
                            <CheckCircle size={16} className="mr-2" /> Aprobar
                          </DropdownMenuItem>
                        )}
                        {hasPermission('members_suspend') && (
                          <>
                            <DropdownMenuSeparator className="bg-zinc-700" />
                            {(member.status === 'suspended' || member.status === 'blocked') ? (
                              <DropdownMenuItem onClick={() => handleActivate(member.id)} className="cursor-pointer text-emerald-500" data-testid={`member-activate-${member.code}`}>
                                <Check size={16} className="mr-2" /> Reactivar
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => handleOpenSuspend(member)} className="cursor-pointer text-orange-400" data-testid={`member-suspend-${member.code}`}>
                                <PauseCircle size={16} className="mr-2" /> Suspender
                              </DropdownMenuItem>
                            )}
                          </>
                        )}
                        {hasPermission('members_delete') && (
                          <DropdownMenuItem onClick={() => handleOpenDelete(member)} className="cursor-pointer text-red-500" data-testid={`member-delete-${member.code}`}>
                            <Trash2 size={16} className="mr-2" /> Eliminar
                          </DropdownMenuItem>
                        )}
                        {(isSuperAdmin || admin?.role === 'gym_admin') && (
                          <>
                            <DropdownMenuSeparator className="bg-zinc-700" />
                            <DropdownMenuItem onClick={async () => {
                              const newMode = member.qr_mode === 'static' ? 'dynamic' : 'static';
                              try { await setMemberQRMode(member.id, newMode); toast.success(`QR ${newMode === 'static' ? 'estatico' : 'dinamico'} asignado`); fetchMembers(); }
                              catch (e) { toast.error('Error'); }
                            }} className="cursor-pointer text-cyan-400" data-testid={`member-qr-${member.code}`}>
                              <QrCode size={16} className="mr-2" /> {member.qr_mode === 'static' ? 'Cambiar a QR Dinamico' : 'Asignar QR Estatico'}
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuSeparator className="bg-zinc-700" />
                        <DropdownMenuItem onClick={() => triggerPhotoUpload(member.id)} disabled={uploadingPhoto} className="cursor-pointer text-violet-400" data-testid={`member-photo-${member.code}`}>
                          <Camera size={16} className="mr-2" /> {uploadingPhoto && photoTargetMemberId === member.id ? 'Subiendo...' : 'Subir Foto'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleOpenDevices(member)} className="cursor-pointer text-zinc-400" data-testid={`member-devices-${member.code}`}>
                          <Smartphone size={16} className="mr-2" /> Dispositivos
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setSelectedMember(member); setShowVideosModal(true); }} className="cursor-pointer text-orange-400" data-testid={`member-videos-${member.code}`}>
                          <Video size={16} className="mr-2" /> Videos de Acceso
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleOpenEmails(member)} className="cursor-pointer text-blue-400" data-testid={`member-emails-${member.code}`}>
                          <Mail size={16} className="mr-2" /> Emails
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setSelectedMember(member); setShowRfidModal(true); setRfidInput(member.rfid_uid || ''); }} className="cursor-pointer text-orange-400" data-testid={`member-rfid-${member.code}`}>
                          <CreditCard size={16} className="mr-2" /> {member.rfid_uid ? 'Cambiar RFID' : 'Grabar Pulsera/Llavero'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Membership Modal */}
      <Dialog open={showMembershipModal} onOpenChange={setShowMembershipModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader><DialogTitle>Asignar Membresía</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-zinc-400">Asignar membresía a <strong>{selectedMember?.name}</strong></p>
            <Select value={selectedPlan} onValueChange={setSelectedPlan}>
              <SelectTrigger className="w-full bg-zinc-800 border-zinc-700" data-testid="plan-select">
                <SelectValue placeholder="Seleccionar plan" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700">
                {plans.map((plan) => (
                  <SelectItem key={plan.id} value={plan.id}>{plan.name} - ${plan.price} ({plan.duration_days} días)</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={async () => {
              if (!selectedPlan || !selectedMember) { toast.error('Selecciona un plan'); return; }
              try { await createMembership({ member_id: selectedMember.id, plan_id: selectedPlan }); toast.success('Membresía asignada'); setShowMembershipModal(false); setSelectedPlan(''); }
              catch (error) { toast.error('Error al asignar membresía'); }
            }} className="w-full btn-gym-primary" data-testid="assign-membership-btn">
              <CreditCard size={20} className="mr-2" /> Asignar Membresía
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar Socio</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Nombre</label>
              <Input value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                className="input-dark" data-testid="edit-member-name" />
            </div>
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Email</label>
              <Input type="email" value={editData.email} onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                className="input-dark" data-testid="edit-member-email" />
            </div>
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Teléfono</label>
              <Input value={editData.phone} onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                className="input-dark" />
            </div>

            {/* Datos personales */}
            <div className="border-t border-zinc-800 pt-4">
              <p className="text-xs text-orange-500 mb-3 uppercase tracking-wider font-semibold">Datos personales</p>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">DNI / NIE / Pasaporte</label>
                  <Input value={editData.document_id} onChange={(e) => setEditData({ ...editData, document_id: e.target.value })}
                    placeholder="12345678A" className="input-dark" data-testid="edit-member-document" />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Direccion</label>
                  <Input value={editData.address} onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                    placeholder="Calle, numero, piso..." className="input-dark" data-testid="edit-member-address" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 block">Ciudad</label>
                    <Input value={editData.city} onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                      placeholder="Madrid" className="input-dark" data-testid="edit-member-city" />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 block">Codigo Postal</label>
                    <Input value={editData.postal_code} onChange={(e) => setEditData({ ...editData, postal_code: e.target.value })}
                      placeholder="28001" className="input-dark" data-testid="edit-member-postal" />
                  </div>
                </div>
              </div>
            </div>

            {/* Licencia */}
            <div className="border-t border-zinc-800 pt-4">
              <p className="text-xs text-orange-500 mb-3 uppercase tracking-wider font-semibold">Licencia</p>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Codigo de Licencia</label>
                  <Input value={editData.license_code} onChange={(e) => setEditData({ ...editData, license_code: e.target.value })}
                    placeholder="LIC-2026-0001" className="input-dark" data-testid="edit-member-license-code" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 block">Fecha de Expedicion</label>
                    <Input type="date" value={editData.license_issue_date} onChange={(e) => setEditData({ ...editData, license_issue_date: e.target.value })}
                      className="input-dark" data-testid="edit-member-license-issue" />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 block">Fecha de Caducidad</label>
                    <Input type="date" value={editData.license_expiry_date} onChange={(e) => setEditData({ ...editData, license_expiry_date: e.target.value })}
                      className="input-dark" data-testid="edit-member-license-expiry" />
                  </div>
                </div>
              </div>
            </div>

            {/* Guest Config */}
            <div className="border-t border-zinc-800 pt-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={editData.can_bring_guests} onChange={(e) => setEditData({ ...editData, can_bring_guests: e.target.checked })} className="w-4 h-4 rounded" data-testid="edit-guest-toggle" />
                <span className="text-sm">Puede traer invitados</span>
              </label>
              {editData.can_bring_guests && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Max invitados/mes</label>
                    <Input type="number" min="1" max="10" value={editData.max_guests_per_month} onChange={(e) => setEditData({ ...editData, max_guests_per_month: parseInt(e.target.value) || 2 })} className="input-dark" data-testid="edit-max-guests" />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Dias validez invitacion</label>
                    <Input type="number" min="1" max="30" value={editData.guest_valid_days} onChange={(e) => setEditData({ ...editData, guest_valid_days: parseInt(e.target.value) || 1 })} className="input-dark" data-testid="edit-guest-days" />
                  </div>
                </div>
              )}
            </div>

            {/* Respuestas de formularios personalizados */}
            {memberFormResponses && memberFormResponses.length > 0 && (
              <div className="border-t border-zinc-800 pt-4">
                <p className="text-xs text-orange-500 mb-3 uppercase tracking-wider font-semibold">
                  Respuestas a formularios ({memberFormResponses.length})
                </p>
                <div className="space-y-3" data-testid="member-form-responses">
                  {memberFormResponses.map((fr) => (
                    <details key={fr.id} className="bg-zinc-950 border border-zinc-800 rounded-lg overflow-hidden">
                      <summary className="px-3 py-2 cursor-pointer text-sm font-medium hover:bg-zinc-900 flex items-center justify-between">
                        <span className="text-zinc-200">{fr.form_title || 'Formulario'}</span>
                        <span className="text-xs text-zinc-500">
                          {fr.created_at ? new Date(fr.created_at).toLocaleDateString('es-ES') : ''}
                        </span>
                      </summary>
                      <div className="px-3 py-3 border-t border-zinc-800 space-y-2">
                        {fr.responses && Object.keys(fr.responses).length > 0 ? (
                          Object.entries(fr.responses).map(([key, value]) => (
                            <div key={key} className="text-sm">
                              <div className="text-xs text-zinc-500">{key}</div>
                              <div className="text-zinc-200 break-words">{Array.isArray(value) ? value.join(', ') : (value || '-')}</div>
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-zinc-500 italic">Sin respuestas</p>
                        )}
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            )}

            <Button onClick={handleUpdateMember} className="w-full btn-gym-primary" data-testid="update-member-btn">
              <Pencil size={20} className="mr-2" /> Guardar Cambios
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Suspend Modal */}
      <Dialog open={showSuspendModal} onOpenChange={setShowSuspendModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader><DialogTitle>Suspender Socio</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex items-center gap-3 p-3 bg-orange-500/10 rounded-lg border border-orange-500/20">
              <PauseCircle size={24} className="text-orange-400 shrink-0" />
              <div>
                <p className="font-medium">{selectedMember?.name}</p>
                <p className="text-sm text-zinc-400">Código: {selectedMember?.code}</p>
              </div>
            </div>
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Motivo de la suspensión *</label>
              <Textarea value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="Ej: Falta de pago, conducta inapropiada, solicitud del socio..."
                className="bg-zinc-800 border-zinc-700 min-h-[100px]" data-testid="suspend-reason-input" />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 border-zinc-700" onClick={() => setShowSuspendModal(false)}>Cancelar</Button>
              <Button className="flex-1 bg-orange-600 hover:bg-orange-700 text-white" onClick={handleSuspend} data-testid="confirm-suspend-btn">
                <Ban size={18} className="mr-2" /> Suspender
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={32} className="text-red-500" />
            </div>
            <h2 className="text-xl font-bold mb-2">Eliminar Socio</h2>
            <p className="text-zinc-400 mb-2">¿Desea confirmar la eliminación del socio?</p>
            <p className="text-lg font-bold text-white mb-2">{selectedMember?.name}</p>
            <p className="text-sm text-zinc-500 mb-4">Código: {selectedMember?.code}</p>
            <p className="text-red-400 text-sm mb-6">Se eliminarán TODOS sus datos: membresías, reservas, accesos e invitados. Esta acción no se puede deshacer.</p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 border-zinc-700" onClick={() => setShowDeleteConfirm(false)} data-testid="cancel-delete-member-btn">Cancelar</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handleDelete} data-testid="confirm-delete-member-btn">
                <Trash2 size={18} className="mr-2" /> Sí, Eliminar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader><DialogTitle>Registrar Pago</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex items-center gap-3 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
              <Receipt size={24} className="text-emerald-400 shrink-0" />
              <div>
                <p className="font-medium">{selectedMember?.name}</p>
                <p className="text-sm text-zinc-400">Código: {selectedMember?.code}</p>
              </div>
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Plan</label>
              <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                <SelectTrigger className="w-full bg-zinc-800 border-zinc-700" data-testid="payment-plan-select">
                  <SelectValue placeholder="Seleccionar plan" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  {plans.map((plan) => (
                    <SelectItem key={plan.id} value={plan.id}>{plan.name} - ${plan.price} ({plan.duration_days} días)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Método de Pago</label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="w-full bg-zinc-800 border-zinc-700" data-testid="payment-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  <SelectItem value="cash">Efectivo</SelectItem>
                  <SelectItem value="card_reception">Tarjeta en Recepción</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Notas (opcional)</label>
              <Input value={paymentNotes} onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="Ej: Pago parcial, descuento aplicado..."
                className="input-dark" data-testid="payment-notes-input" />
            </div>
            
            {selectedPlan && (
              <div className="p-4 bg-zinc-800 rounded-lg text-center">
                <p className="text-sm text-zinc-400">Total a cobrar</p>
                <p className="text-3xl font-black" style={{ color: 'var(--gym-primary)' }}>
                  ${plans.find(p => p.id === selectedPlan)?.price?.toFixed(2) || '0.00'}
                </p>
              </div>
            )}
            
            <Button onClick={handleManualPayment} className="w-full btn-gym-primary" data-testid="confirm-payment-btn">
              <Banknote size={20} className="mr-2" /> Registrar Pago y Activar Membresía
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Devices Modal */}
      <Dialog open={showDevicesModal} onOpenChange={setShowDevicesModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone size={20} className="text-blue-400" />
              Dispositivos de {devicesMember?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {memberDevices.length === 0 ? (
              <p className="text-zinc-500 text-sm text-center py-4">No hay dispositivos registrados</p>
            ) : (
              <>
                <div className="flex items-center justify-between text-xs text-zinc-500">
                  <span>{memberDevices.filter(d => d.active).length} activos / {memberDevices.length} total</span>
                  {memberDevices.some(d => d.active) && (
                    <button onClick={handleDeactivateAll} className="text-red-400 hover:text-red-300 underline" data-testid="deactivate-all-devices">
                      Desactivar todos
                    </button>
                  )}
                </div>
                {memberDevices.map(d => (
                  <div key={d.id} className={`flex items-center gap-3 p-3 rounded-lg border ${d.active ? 'bg-zinc-800/50 border-zinc-700' : 'bg-zinc-900 border-zinc-800 opacity-50'}`}>
                    <Smartphone size={20} className={d.active ? 'text-blue-400' : 'text-zinc-600'} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">{d.device_name}</p>
                      <p className="text-[11px] text-zinc-500 truncate">{d.user_agent?.substring(0, 50)}...</p>
                      <p className="text-[10px] text-zinc-600">{d.active ? `Ultimo uso: ${d.last_active?.substring(0, 10)}` : 'Desactivado'}</p>
                    </div>
                    {d.active && (
                      <button onClick={() => handleDeactivateDevice(d.id)}
                        className="text-xs text-red-400 hover:text-red-300 px-2 py-1 border border-red-500/20 rounded hover:bg-red-500/10 transition-colors"
                        data-testid={`deactivate-device-${d.id}`}>
                        Desactivar
                      </button>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Emails Modal */}
      <Dialog open={showEmailsModal} onOpenChange={setShowEmailsModal}>
        <DialogContent style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }} className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail size={18} /> Emails de {selectedMember?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 space-y-3 max-h-[400px] overflow-y-auto" data-testid="emails-list">
            {memberEmails.length === 0 ? (
              <p className="text-center py-6" style={{ color: 'var(--text-muted)' }}>No hay emails enviados a este socio</p>
            ) : memberEmails.map(email => (
              <div key={email.id} className="p-3 rounded-lg flex items-center justify-between" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)' }}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{email.subject}</p>
                  <div className="flex items-center gap-2 text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                    <span>{new Date(email.sent_at).toLocaleString('es')}</span>
                    <span className={`px-1.5 py-0.5 rounded ${email.status === 'sent' ? 'bg-emerald-900/30 text-emerald-400' : 'bg-red-900/30 text-red-400'}`}>
                      {email.status === 'sent' ? 'Enviado' : 'Fallido'}
                    </span>
                    <span className="px-1.5 py-0.5 rounded" style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}>{email.email_type}</span>
                  </div>
                </div>
                {email.status === 'sent' && (
                  <button
                    onClick={() => handleResendEmail(email.id)}
                    disabled={resendingId === email.id}
                    className="shrink-0 ml-2 px-3 py-1.5 text-xs rounded-lg transition-colors flex items-center gap-1"
                    style={{ background: 'var(--bg-secondary)', color: 'var(--gym-primary)', border: '1px solid var(--border-secondary)' }}
                    data-testid={`resend-email-${email.id}`}
                  >
                    {resendingId === email.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                    Reenviar
                  </button>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Members Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet size={18} /> Importar Socios desde Excel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Gimnasio destino</label>
              <Select value={importGymId} onValueChange={setImportGymId}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="import-gym-select">
                  <SelectValue placeholder="Seleccionar gimnasio" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  {gyms.map((g) => (
                    <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Archivo Excel (.xlsx)</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="hidden"
                data-testid="import-file-input"
              />
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-zinc-700 rounded-xl p-6 text-center cursor-pointer hover:border-zinc-500 transition-colors"
              >
                {importFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileSpreadsheet size={24} className="text-emerald-400" />
                    <div className="text-left">
                      <p className="font-medium">{importFile.name}</p>
                      <p className="text-xs text-zinc-500">{(importFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload size={32} className="mx-auto text-zinc-600 mb-2" />
                    <p className="text-zinc-400 text-sm">Haz clic para seleccionar archivo</p>
                    <p className="text-zinc-600 text-xs mt-1">Formato: .xlsx</p>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="keepCodes"
                checked={importKeepCodes}
                onChange={(e) => setImportKeepCodes(e.target.checked)}
                className="rounded"
                data-testid="import-keep-codes"
              />
              <label htmlFor="keepCodes" className="text-sm text-zinc-300">
                Mantener codigos de socio originales
              </label>
            </div>

            <div className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
              <p className="text-xs text-zinc-400">
                El Excel debe tener headers en la primera fila. Columnas reconocidas: 
                <span className="text-zinc-300"> Nombre Completo, Email, Telefono, DNI, Codigo, Fecha Alta, Cuota, Activo</span>.
                Los socios con email duplicado se omitiran.
              </p>
            </div>

            {importResult && (
              <div className={`p-4 rounded-lg border ${importResult.success ? 'bg-emerald-900/20 border-emerald-700' : 'bg-red-900/20 border-red-700'}`}>
                <p className="font-medium text-sm">{importResult.message}</p>
                {importResult.imported > 0 && (
                  <div className="mt-2 text-xs space-y-1 text-zinc-300">
                    <p>Importados: <span className="text-emerald-400 font-bold">{importResult.imported}</span></p>
                    <p>Omitidos (duplicados): <span className="text-yellow-400">{importResult.skipped}</span></p>
                    {importResult.errors > 0 && <p>Errores: <span className="text-red-400">{importResult.errors}</span></p>}
                  </div>
                )}
                {importResult.error_details?.length > 0 && (
                  <div className="mt-2 text-xs text-red-400 max-h-20 overflow-y-auto">
                    {importResult.error_details.map((e, i) => <p key={i}>{e}</p>)}
                  </div>
                )}
              </div>
            )}

            <Button 
              onClick={handleImport} 
              disabled={importing || !importFile || !importGymId}
              className="w-full btn-gym-primary"
              data-testid="import-submit-btn"
            >
              {importing ? (
                <><Loader2 size={16} className="mr-2 animate-spin" /> Importando...</>
              ) : (
                <><Upload size={16} className="mr-2" /> Importar Socios</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Memberships Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet size={18} /> Asignar Membresias Masivamente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Gimnasio</label>
              <Select value={assignGymId} onValueChange={setAssignGymId}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="assign-gym-select">
                  <SelectValue placeholder="Seleccionar gimnasio" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  {gyms.map((g) => (
                    <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Archivo de vencimientos (.json)</label>
              <input
                ref={assignFileRef}
                type="file"
                accept=".json"
                onChange={(e) => {
                  const f = e.target.files[0];
                  setAssignFile(f);
                  if (f) {
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      try {
                        const data = JSON.parse(ev.target.result);
                        setAssignPreview(data);
                        const total = Object.keys(data).length;
                        const conFecha = Object.values(data).filter(v => v.fecha_hasta).length;
                        toast.info(`${total} socios encontrados, ${conFecha} con fecha de vencimiento`);
                      } catch { setAssignPreview(null); toast.error('JSON invalido'); }
                    };
                    reader.readAsText(f);
                  }
                }}
                className="hidden"
                data-testid="assign-file-input"
              />
              <div
                onClick={() => assignFileRef.current?.click()}
                className="border-2 border-dashed border-zinc-700 rounded-xl p-6 text-center cursor-pointer hover:border-zinc-500 transition-colors"
              >
                {assignFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileSpreadsheet size={24} className="text-amber-400" />
                    <div className="text-left">
                      <p className="font-medium">{assignFile.name}</p>
                      <p className="text-xs text-zinc-500">
                        {assignPreview ? `${Object.keys(assignPreview).length} socios, ${Object.values(assignPreview).filter(v => v.fecha_hasta).length} con vencimiento` : ''}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload size={32} className="mx-auto text-zinc-600 mb-2" />
                    <p className="text-zinc-400 text-sm">Sube el archivo vencimientos_lafabrika.json</p>
                  </>
                )}
              </div>
            </div>

            <div className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
              <p className="text-xs text-zinc-400">
                Asigna membresias a los socios importados usando sus <span className="text-zinc-300">fechas de vencimiento reales</span> y <span className="text-zinc-300">cuota</span> para asociar el plan correcto. Los socios sin cuota activa se omiten.
              </p>
            </div>

            {assignResult && (
              <div className={`p-4 rounded-lg border ${assignResult.success ? 'bg-emerald-900/20 border-emerald-700' : 'bg-red-900/20 border-red-700'}`}>
                <p className="font-medium text-sm">{assignResult.message}</p>
                {assignResult.assigned > 0 && (
                  <div className="mt-2 text-xs space-y-1 text-zinc-300">
                    <p>Membresias creadas: <span className="text-emerald-400 font-bold">{assignResult.assigned}</span></p>
                    <p>Omitidos: <span className="text-yellow-400">{assignResult.skipped}</span></p>
                    {assignResult.no_plan > 0 && <p>Sin plan: <span className="text-red-400">{assignResult.no_plan}</span></p>}
                  </div>
                )}
              </div>
            )}

            <Button
              onClick={handleAssignBulk}
              disabled={assigning || !assignPreview || !assignGymId}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="assign-submit-btn"
            >
              {assigning ? (
                <><Loader2 size={16} className="mr-2 animate-spin" /> Asignando...</>
              ) : (
                <><FileSpreadsheet size={16} className="mr-2" /> Asignar Membresias</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Membership Expiration Modal */}
      <Dialog open={showEditExpirationModal} onOpenChange={setShowEditExpirationModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle>Modificar Vencimiento</DialogTitle>
          </DialogHeader>
          {membershipMember && (
            <div className="space-y-4 mt-4">
              <div className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <p className="font-medium">{membershipMember.name}</p>
                <p className="text-xs text-zinc-400">
                  Plan: {membershipMember.membership?.plan_name || 'Sin plan'}
                  {membershipMember.membership?.end_date && (
                    <> · Vence: {new Date(membershipMember.membership.end_date).toLocaleDateString('es-ES')}</>
                  )}
                </p>
              </div>

              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Nueva fecha de vencimiento</label>
                <Input
                  type="date"
                  value={membershipDate}
                  onChange={(e) => setMembershipDate(e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="membership-date-input"
                />
              </div>

              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Comentario (opcional)</label>
                <Textarea
                  value={membershipComment}
                  onChange={(e) => setMembershipComment(e.target.value)}
                  placeholder="Razon del cambio..."
                  className="bg-zinc-800 border-zinc-700 min-h-[60px]"
                  data-testid="membership-comment-input"
                />
              </div>

              <Button
                onClick={handleUpdateMembership}
                disabled={savingMembership || !membershipDate}
                className="w-full btn-gym-primary"
                data-testid="membership-save-btn"
              >
                {savingMembership ? (
                  <><Loader2 size={16} className="mr-2 animate-spin" /> Guardando...</>
                ) : (
                  'Guardar Cambio'
                )}
              </Button>

              {membershipLogs.length > 0 && (
                <div data-testid="membership-logs-section">
                  <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Historial de cambios</p>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {membershipLogs.map((log) => (
                      <div key={log.id} className="p-2.5 rounded-lg bg-zinc-800/50 border border-zinc-700/50 text-xs">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-zinc-300 font-medium">{log.changed_by}</span>
                          <span className="text-zinc-500">{new Date(log.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <div className="flex items-center gap-2 text-zinc-400">
                          <span>{log.previous_end_date ? new Date(log.previous_end_date).toLocaleDateString('es-ES') : '-'}</span>
                          <span className="text-zinc-600">&rarr;</span>
                          <span className="text-emerald-400 font-medium">{new Date(log.new_end_date).toLocaleDateString('es-ES')}</span>
                        </div>
                        {log.comment && (
                          <p className="mt-1 text-zinc-400 italic">"{log.comment}"</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Videos Modal */}
      <Dialog open={showVideosModal} onOpenChange={setShowVideosModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video size={20} style={{ color: '#FF6600' }} />
              Videos de Acceso — {selectedMember?.name}
            </DialogTitle>
          </DialogHeader>
          {loadingVideos ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-zinc-500" size={32} />
            </div>
          ) : memberVideos.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <Video size={48} className="mx-auto mb-4 opacity-30" />
              <p>No hay grabaciones de acceso para este socio</p>
              <p className="text-xs mt-2 text-zinc-600">Los videos se graban automaticamente al escanear el QR en el torno</p>
            </div>
          ) : (
            <div className="space-y-3">
              {playingVideoUrl && (
                <div className="rounded-xl overflow-hidden bg-black mb-4">
                  <video src={playingVideoUrl} controls autoPlay className="w-full" style={{ maxHeight: '300px' }} data-testid="member-video-player" />
                </div>
              )}
              {memberVideos.map((log) => (
                <div key={log.id} className="flex items-center justify-between p-3 rounded-xl bg-zinc-800/50 border border-zinc-700/50">
                  <div>
                    <p className="font-medium text-sm">{new Date(log.timestamp).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                    <p className="text-xs text-zinc-500">{new Date(log.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const API = process.env.REACT_APP_BACKEND_URL;
                      const token = localStorage.getItem('token');
                      setPlayingVideoUrl(`${API}/api/access/video/${log.video_id}?token=${token}`);
                    }}
                    className="text-orange-400 hover:text-orange-300"
                    data-testid={`play-video-${log.id}`}
                  >
                    <Video size={16} className="mr-1" /> Ver
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* RFID Live Capture Modal */}
      <Dialog open={showRfidModal} onOpenChange={(open) => { setShowRfidModal(open); setRfidListening(false); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard size={20} style={{ color: '#FF6600' }} />
              Grabar Pulsera / Llavero RFID
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700/50">
              <p className="text-sm text-zinc-400 mb-1">Socio:</p>
              <p className="font-bold">{selectedMember?.name}</p>
              {selectedMember?.rfid_uid && (
                <p className="text-xs text-orange-400 mt-1">RFID actual: {selectedMember.rfid_uid}</p>
              )}
            </div>

            {rfidListening ? (
              <div className="text-center py-6">
                <div className="w-20 h-20 mx-auto rounded-full border-4 border-orange-500 flex items-center justify-center mb-4 animate-pulse" style={{ boxShadow: '0 0 30px rgba(255,102,0,0.3)' }}>
                  <CreditCard size={32} style={{ color: '#FF6600' }} />
                </div>
                <p className="font-bold text-lg mb-1" style={{ color: '#FF6600', fontFamily: 'Outfit' }}>Esperando lectura...</p>
                <p className="text-xs text-zinc-500">Pasa la pulsera o llavero por el lector RFID</p>
                <input
                  autoFocus
                  value={rfidInput}
                  onChange={(e) => setRfidInput(e.target.value.toUpperCase())}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter' && rfidInput.trim()) {
                      setSavingRfid(true);
                      try {
                        await assignRFID(selectedMember.id, rfidInput.trim());
                        toast.success('Pulsera/llavero grabado correctamente');
                        setShowRfidModal(false);
                        setRfidListening(false);
                        setRfidInput('');
                        fetchMembers();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Error al grabar RFID');
                      } finally { setSavingRfid(false); }
                    }
                  }}
                  className="mt-4 w-full text-center text-2xl font-mono tracking-widest bg-black border-2 border-orange-500/50 rounded-xl px-4 py-3 text-orange-400 focus:outline-none focus:border-orange-500"
                  style={{ caretColor: '#FF6600' }}
                  placeholder="..."
                  data-testid="rfid-live-input"
                />
                <p className="text-[10px] text-zinc-600 mt-2">El lector escribe automaticamente el UID</p>
              </div>
            ) : (
              <div className="space-y-3">
                <button
                  onClick={() => { setRfidListening(true); setRfidInput(''); }}
                  className="w-full py-4 rounded-xl font-bold text-lg transition-all"
                  style={{ background: 'linear-gradient(135deg, #FF6600, #E65C00)', color: '#FFF', fontFamily: 'Outfit' }}
                  data-testid="rfid-start-listen-btn"
                >
                  Iniciar Grabacion
                </button>
                <p className="text-xs text-zinc-500 text-center">O escribe el UID manualmente:</p>
                <div className="flex gap-2">
                  <Input
                    value={rfidInput}
                    onChange={(e) => setRfidInput(e.target.value.toUpperCase())}
                    placeholder="UID del RFID"
                    className="input-dark font-mono"
                    data-testid="rfid-manual-input"
                  />
                  <Button
                    onClick={async () => {
                      if (!rfidInput.trim()) return;
                      setSavingRfid(true);
                      try {
                        await assignRFID(selectedMember.id, rfidInput.trim());
                        toast.success(rfidInput.trim() ? 'RFID asignado' : 'RFID eliminado');
                        setShowRfidModal(false); setRfidInput(''); fetchMembers();
                      } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
                      finally { setSavingRfid(false); }
                    }}
                    disabled={savingRfid}
                    className="btn-gym-primary shrink-0"
                    data-testid="rfid-save-btn"
                  >
                    {savingRfid ? <Loader2 size={16} className="animate-spin" /> : 'Guardar'}
                  </Button>
                </div>
                {selectedMember?.rfid_uid && (
                  <button
                    onClick={async () => {
                      try {
                        await assignRFID(selectedMember.id, '');
                        toast.success('RFID eliminado');
                        setShowRfidModal(false); fetchMembers();
                      } catch (err) { toast.error('Error al eliminar'); }
                    }}
                    className="w-full text-center text-xs text-red-400 hover:text-red-300 py-2"
                    data-testid="rfid-remove-btn"
                  >
                    Eliminar RFID actual
                  </button>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Global hidden file input for member photo uploads (works on mobile) */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handlePhotoSelected}
        data-testid="member-photo-input"
      />

    </div>
  );
}
